import {
  Badge,
  Card,
  FluentProvider,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useActiveTheme } from '@moko/ui';
import { formatDateOnlyForDisplay } from '../../utils/dateOnly';
import {
  formatParameterValue,
  inheritanceLabel,
  parameterLabel,
  type ParameterEffectiveRow,
} from './pricingParameterHelpers';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: tokens.spacingHorizontalM,
    backgroundColor: 'transparent',
  },
  card: {
    gap: tokens.spacingVerticalXS,
    boxShadow: tokens.shadow4,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  value: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightHero700,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
});

export interface PricingParameterEffectiveCardsProps {
  rows: ParameterEffectiveRow[];
  loading: boolean;
}

const PricingParameterEffectiveCards = ({
  rows,
  loading,
}: PricingParameterEffectiveCardsProps) => {
  const theme = useActiveTheme();
  const styles = useStyles();

  return (
    <FluentProvider theme={theme} style={{ background: 'transparent' }}>
      {loading ? (
        <Spinner size="small" label="Loading effective values" />
      ) : rows.length > 0 && rows.every(({ record }) => !record) ? (
        <Text className={styles.muted}>
          No value applies to this pricing item on this date. Add a global
          default or a tenant override.
        </Text>
      ) : (
        <div className={styles.grid}>
          {rows.map(({ id, definition, record }) => (
            <Card key={id} className={styles.card}>
              <Text className={styles.muted}>
                {parameterLabel(definition.key)}
              </Text>
              <Text className={styles.value}>
                {record ? formatParameterValue(definition, record.value) : '—'}
              </Text>
              <div>
                <Badge
                  appearance={record?.source === 'Tenant' ? 'filled' : 'tint'}
                  color={
                    record?.source === 'Tenant'
                      ? 'brand'
                      : record
                      ? 'informative'
                      : 'subtle'
                  }
                >
                  {inheritanceLabel(record)}
                </Badge>
              </div>
              <Text size={200} className={styles.muted}>
                {record
                  ? `${record.source} value, ${formatDateOnlyForDisplay(
                      record.effectiveFrom
                    )} to ${
                      formatDateOnlyForDisplay(record.effectiveTo) ||
                      'no end date'
                    }`
                  : 'No tenant or global value applies.'}
              </Text>
            </Card>
          ))}
        </div>
      )}
    </FluentProvider>
  );
};

export default PricingParameterEffectiveCards;
